<template>
  <div class="shape-format">
    <h2 class="shape-format-title">图标属性</h2>
    <color-gradient-editor
      label="填充"
      showAlpha
      v-model="currentEffect.content.color"
      :gradient.sync="currentEffect.content.gradient"
      colorFormat="rgba"
    />
    <LabelOption
      label="风格类型"
      :optionList="themes"
      v-model="currentEffect.content.theme"
    />
    <color-gradient-editor
      label="内容填充"
      showAlpha
      v-model="currentEffect.content.secondaryColor"
      :gradient.sync="currentEffect.content.secondaryGradient"
      colorFormat="rgba"
      v-if="currentEffect.content.theme === 'TwoTone'"
    />
    <LabelSwitch label="阴影" v-model="currentEffect.content.shadow" />
    <template v-if="currentEffect.content.shadow">
      <div class="row-box">
        <LabelColor
          label="颜色"
          class="text-shadow-color"
          v-model="currentEffect.content.shadowColor"
          :showAlpha="false"
        />
        <LabelInput
          label="模糊"
          class="text-shadow-blurry"
          v-model="currentEffect.content.shadowBlur"
          :min="0"
        />
      </div>
      <div class="row-box">
        <div class="setting">
          <label>偏移</label>
        </div>
        <LabelInput
          label="x"
          class="text-shadow-offset-x"
          v-model="currentEffect.content.shadowX"
        />
        <LabelInput
          label="y"
          class="text-shadow-offset-y"
          v-model="currentEffect.content.shadowY"
        />
      </div>
    </template>
    <LabelSlider
      label="透明度"
      class="text-shadow-opacity"
      :value="effectInfo.opacity"
      type="opacity"
    />
    <LabelInput
      label="旋转"
      :deg="true"
      v-model="currentEffect.content.rotate"
      :precision="0"
    />
    <flip-editor v-model="currentEffect.content.flip" />
    <position-editor :effect="currentEffect" />
    <label-button text="适应画布" @click="fitCanvas">大小</label-button>
    <template v-if="!currentEffect.container">
      <div class="border-bottom"></div>
      <h2 class="shape-format-title" style="margin-top: 12px">转场效果</h2>
      <LabelInput label="延迟出现（秒）" v-model="displayStart" :time="true" />
      <LabelInput
        label="显示时长（秒）"
        v-model="displayDuration"
        :time="true"
      />
      <LabelOption
        label="入场效果"
        :optionList="enterEffectList"
        v-model="currentEffect.enter"
      />
      <LabelInput
        label="入场效果显示时长（秒）"
        :time="true"
        v-model="currentEffect.enterDuration"
        :disabled="currentEffect.enter === 'none'"
      />
      <LabelOption
        label="出场效果"
        :optionList="leaveEffectList"
        v-model="currentEffect.leave"
      />
      <LabelInput
        label="出场效果显示时长（秒）"
        :time="true"
        v-model="currentEffect.leaveDuration"
        :disabled="currentEffect.leave === 'none'"
      />
      <div class="border-bottom"></div>

      <h2 class="shape-format-title" style="margin-top: 12px">动态效果</h2>
      <LabelSwitch
        :fn="fn"
        label="动态特效"
        v-model="currentEffect.showTracks"
      />
      <SingleEffectEditor
        label="特效"
        v-for="(track, i) in currentEffect.tracks"
        :disabled="!currentEffect.showTracks"
        :key="i"
        :track="track"
        :effectList="effectListAt(i)"
        @delete="removeTrack(i)"
        :deletable="currentEffect.tracks.length > 1"
      />
      <a-button
        v-if="currentEffect.tracks.length < 5"
        class="insert-track"
        @click="insertTrack"
        block
        :disabled="!currentEffect.showTracks"
        icon="plus"
        >继续添加</a-button
      >
    </template>
    <a-modal
      v-if="currentEffect.content.shape === 'path'"
      v-model="modalOpen"
      :title="false"
      @ok="handleOK"
      :footer="null"
    >
      <a-form @submit.prevent="done" :form="form">
        <a-tabs class="path-editor-tabs" :tabBarGutter="0" v-model="currentTab">
          <a-tab-pane key="1" tab="编辑">
            <p>
              编辑
              <a
                href="https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d"
                target="_blank"
              >
                <code>d</code> 属性
              </a>
            </p>
            <a-form-item>
              <a-textarea
                v-model="currentEffect.content.d"
                :autosize="{ minRows: 2 }"
              />
            </a-form-item>
          </a-tab-pane>
          <a-tab-pane key="2" tab="转换">
            <p>
              转换 DrawingML
              <a
                href="https://docs.microsoft.com/en-us/dotnet/api/documentformat.openxml.drawing.path?view=openxml-2.8.1"
                target="_blank"
              >
                <code>&lt;a:path&gt;</code> 元素
              </a>
            </p>
            <a-form-item>
              <a-textarea
                v-decorator="[
                  'input',
                  { rules: [{ required: true, message: 'Input something' }] }
                ]"
                :autosize="{ minRows: 2 }"
              />
            </a-form-item>
          </a-tab-pane>
        </a-tabs>
        <a-button type="primary" html-type="submit">完成</a-button>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import LabelInput from "../base/LabelInput";
import LabelOption from "../base/LabelOption";
import LabelSwitch from "../base/LabelSwitch";
import LabelColor from "../base/LabelColor";
import LabelSlider from "../base/LabelSlider";
import LabelButton from '../base/LabelButton';
import SingleEffectEditor from '../base/SingleEffectEditor';
import PositionEditor from '../base/PositionEditor';
import FlipEditor from '../base/FlipEditor';
import ColorGradientEditor from '../base/ColorGradientEditor';
import { effectParams } from '../../utils/textEffectMap';
import { fitCanvas } from '../../utils/util';
import iconList, { themeNames } from '../../utils/iconList';

export default {
  name: "TextFormat",
  components: {
    LabelInput,
    LabelOption,
    LabelSwitch,
    LabelButton,
    LabelColor,
    LabelSlider,
    SingleEffectEditor,
    PositionEditor,
    FlipEditor,
    ColorGradientEditor
  },
  data() {
    return {
      modalOpen: false,
      currentTab: '1',
      enterEffectList: [
        { name: '无', value: 'none' },
        { name: '淡入', value: 'enter' }
      ],
      leaveEffectList: [
        { name: '无', value: 'none' },
        { name: '淡出', value: 'enter' }
      ],
      form: this.$form.createForm(this, { name: 'test' })
    };
  },
  computed: {
    ...mapGetters(['effectInfo', 'currentEffect', 'activeFragment', 'aspectRatio']),
    ...mapState(['effectNames', 'generalEffects']),
    displayStart: {
      get() {
        return this.currentEffect.start;
      },
      set(value) {
        this.$set(this.currentEffect, 'start', Math.max(value, 0));
      }
    },
    displayDuration: {
      get() {
        return this.currentEffect.duration;
      },
      set(value) {
        this.$set(this.currentEffect, 'duration', Math.max(value, 400));
      }
    },
    themes() {
      const themes = iconList[this.currentEffect.content.icon];
      return themes.map(item => ({
        name: themeNames[item],
        value: item
      }));
    }
  },
  methods: {
    fn(showTracks) {
      if (showTracks) {
        this.$set(this.currentEffect.tracks[0], 'start', this.currentEffect.start);
      }
    },
    insertTrack() {
      let effectType = '';
      for (var i of this.generalEffects) {
        if (this.currentEffect.tracks.every(tr => {
          switch (tr.effectType) {
            case 'slide':
            case 'slideHorizon':
              return i !== 'slide' && i !== 'slideHorizon';
          }
          return i !== tr.effectType;
        })) {
          effectType = i;
          break;
        }
      }
      if (effectType) {
        this.currentEffect.tracks.push({
          id: +new Date() + '',
          effectType,
          params: effectParams[effectType] ? { ...effectParams[effectType] } : null,
          start: 1e3,
          duration: 1e3
        });
      } else {
        console.log('No effect was added');
      }
    },
    removeTrack(i) {
      this.$set(
        this.currentEffect,
        'tracks',
        this.currentEffect.tracks.slice(0, i).concat(this.currentEffect.tracks.slice(i + 1))
      )
    },
    effectListAt(i) {
      return this.generalEffects
        .map(v => {
          let disabled = false;
          switch (v) {
            case 'slide':
            case 'slideHorizon':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && (tr.effectType === 'slide' || tr.effectType === 'slideHorizon'));
              break;
            case 'scale':
            case 'gradientFade':
            case 'gradientHide':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && tr.effectType === v);
              break;
          }
          return {
            name: this.effectNames[v],
            value: v,
            disabled
          };
        });
    },
    /**
     * fit the image within the canvas' size.
     */
    fitCanvas() {
      const efc = this.currentEffect.content;
      const { top, left, width, height } = fitCanvas(efc, 480 * this.$store.state.screenRatio, 480);

      efc.height = height;
      efc.width = width;
      efc.top = top;
      efc.left = left;
    }
  }
}
</script>

<style scoped src="../shape/ShapeFormat.vue" />